import React, { useState, useEffect, useMemo, memo } from 'react';
import { Loader2 } from 'lucide-react';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { ProcessedMessage } from '../../shared/types';
import { getDataSource } from '../../services/dataSource';
import { FileAttachmentsList } from './FileAttachment';

interface ConversationViewerProps {
  messageRecordIds?: string[];
  leadName?: string;
  className?: string;
  messagesWithProcessingFiles?: {[messageId: string]: Array<{id: string; filename: string; status: 'processing'}>};
}

// Simple cache to store conversation data for the session
const conversationCache = new Map<string, { 
  messages: ProcessedMessage[], 
  conversationSubject: string 
}>();

const MessageCard: React.FC<{
  message: ProcessedMessage;
  processingFiles?: Array<{id: string; filename: string; status: 'processing'}>;
}> = memo(({ message, processingFiles }) => {
  const { t } = useTranslation();

  const translateMessageNote = (note: string): string => {
    // Check if the note matches the Spanish pattern for original recipient message
    const spanishPattern = /^Este mensaje fue enviado originalmente a (.+)\.\s*Se incluye aquí como referencia\.$/;
    const match = note.match(spanishPattern);

    if (match) {
      const email = match[1];
      return t('conversation.messageNote.originallyTo', { email });
    }

    // If no match, return the original note
    return note;
  };

  const formatMessageContent = (content: string): string => {
    const isHtml = /<[a-z][\s\S]*>/i.test(content);

    let formattedContent: string;
    if (isHtml) {
      // HTML content (body_html): auto-link plain URLs that aren't already inside tags
      formattedContent = content.replace(
        /(?<![="'])((https?:\/\/[^\s<>"]+))/gi,
        (_match, url) => {
          const cleanUrl = url.replace(/[.,;:!?)"']+$/, '');
          const trailing = url.slice(cleanUrl.length);
          return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${cleanUrl}</a>${trailing}`;
        }
      );
    } else {
      // Plain text (body_text / cleaned_message): convert \n to <br> and linkify
      formattedContent = content.replace(/\n/g, '<br>');
      const urlRegex = /(https?:\/\/[^\s<>"]+)/gi;
      formattedContent = formattedContent.replace(urlRegex, (url) => {
        const cleanUrl = url.replace(/[.,;:!?)"']+$/, '');
        const trailingPunctuation = url.slice(cleanUrl.length);
        return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${cleanUrl}</a>${trailingPunctuation}`;
      });
    }

    // SECURITY: Sanitize content to prevent XSS attacks
    // Allow block-level tags so HTML email structure (paragraphs, lists, tables) is preserved
    return DOMPurify.sanitize(formattedContent, {
      ALLOWED_TAGS: [
        'br', 'a', 'strong', 'em', 'b', 'i', 'u', 'span',
        'p', 'div', 'blockquote', 'pre',
        'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li',
        'table', 'thead', 'tbody', 'tr', 'td', 'th',
        'hr', 'img',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'src', 'alt', 'width', 'height', 'colspan', 'rowspan'],
      FORBID_TAGS: ['script', 'object', 'embed', 'link', 'style', 'iframe', 'form', 'input'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'style'],
      ADD_ATTR: ['target', 'rel'],
      ALLOW_DATA_ATTR: false
    });
  };

  const buildRecipientsText = (recipients: string, cc: string, cco: string): string => {
    let recipientsHtml = '';

    if (recipients) {
      // Escape HTML in recipient names to prevent XSS
      const safeRecipients = DOMPurify.sanitize(recipients, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      recipientsHtml += `${t('conversation.to')} ${safeRecipients}`;
    } else {
      recipientsHtml += t('conversation.to');
    }

    if (cc) {
      // Escape HTML in CC names to prevent XSS
      const safeCc = DOMPurify.sanitize(cc, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      recipientsHtml += ` &nbsp;|&nbsp; ${t('conversation.cc')} ${safeCc}`;
    }

    if (cco) {
      // Escape HTML in CCO names to prevent XSS
      const safeCco = DOMPurify.sanitize(cco, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
      recipientsHtml += ` &nbsp;|&nbsp; ${t('conversation.bcc')} ${safeCco}`;
    }

    return recipientsHtml;
  };

  const avatarColor = message.isCompanyMessage ? 'bg-blue-800' : 'bg-emerald-600';
  const recipientsText = buildRecipientsText(message.recipients, message.cc, message.cco);

  return (
    <div className={`message-card bg-white p-5 border-b border-gray-200 last:border-b-0 ${
      message.isCompanyMessage ? 'company-message' : 'client-message'
    }`}>
      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="flex">
          <div className="flex-shrink-0 w-14 pr-3">
            <div className={`sender-avatar w-10 h-10 text-center font-semibold text-sm text-white leading-10 ${avatarColor}`}>
              {message.initials}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="message-header flex justify-between items-start mb-1">
              <div className="sender-name font-semibold text-sm text-gray-900">
                {message.displayName}
              </div>
              <div className="message-time text-xs text-gray-500 ml-2 flex-shrink-0">
                {message.formattedTimestamp}
              </div>
            </div>
            
            {recipientsText && (
              <div 
                className="message-recipients text-xs text-gray-500 italic mb-3"
                dangerouslySetInnerHTML={{ __html: recipientsText }}
              />
            )}
            
            {/* Message Note */}
            {message.messageNote && (
              <div className="message-note bg-slate-50 border-l-4 border-slate-300 px-3 py-2 mb-3 text-xs text-slate-600">
                {translateMessageNote(message.messageNote)}
              </div>
            )}
            
            <div 
              className="message-body text-sm leading-relaxed text-gray-700"
              dangerouslySetInnerHTML={{ 
                __html: formatMessageContent(message.messageText) 
              }}
            />
            
            {/* File Attachments */}
            <FileAttachmentsList 
              attachments={message.attachments} 
              processingFiles={processingFiles}
            />
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="block sm:hidden">
        <div className="mobile-header-wrapper flex mb-2">
          <div className="flex-shrink-0 w-12 pr-3">
            <div className={`mobile-avatar w-9 h-9 text-center font-semibold text-xs text-white leading-9 ${avatarColor}`}>
              {message.initials}
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="mobile-sender-name font-semibold text-xs text-gray-900 mb-1">
              {message.displayName}
            </div>
            <div className="mobile-message-time text-xs text-gray-500">
              {message.formattedTimestamp}
            </div>
          </div>
        </div>
        
        {recipientsText && (
          <div 
            className="mobile-recipients text-xs text-gray-500 italic mb-3"
            dangerouslySetInnerHTML={{ __html: recipientsText }}
          />
        )}
        
        {/* Message Note */}
        {message.messageNote && (
          <div className="message-note bg-slate-50 border-l-4 border-slate-300 px-3 py-2 mb-3 text-xs text-slate-600">
            {translateMessageNote(message.messageNote)}
          </div>
        )}
        
        <div 
          className="mobile-body text-xs leading-relaxed text-gray-700"
          dangerouslySetInnerHTML={{ 
            __html: formatMessageContent(message.messageText) 
          }}
        />
        
        {/* File Attachments */}
        <FileAttachmentsList 
          attachments={message.attachments} 
          processingFiles={processingFiles}
        />
      </div>
    </div>
  );
});

MessageCard.displayName = 'MessageCard';

export const ConversationViewer: React.FC<ConversationViewerProps> = memo(({
  messageRecordIds,
  leadName,
  className = "",
  messagesWithProcessingFiles
}) => {
  const { t } = useTranslation();
  const [conversationData, setConversationData] = useState<{
    messages: ProcessedMessage[];
    conversationSubject: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create cache key from message record IDs
  const cacheKey = useMemo(() => {
    if (!messageRecordIds || messageRecordIds.length === 0) return '';
    const key = messageRecordIds.sort().join(',');
    // console.log(`🎭 CONVVIEWER: Generated cache key: "${key.substring(0, 50)}${key.length > 50 ? '...' : ''}" (${messageRecordIds.length} message IDs)`);
    // console.log(`📋 CONVVIEWER: Message IDs received:`, messageRecordIds);
    return key;
  }, [messageRecordIds]);

  useEffect(() => {
    const fetchConversation = async () => {
      if (!messageRecordIds || messageRecordIds.length === 0) {
        setConversationData({ messages: [], conversationSubject: '' });
        return;
      }

      // Check preloaded data first (fastest)
      // console.log(`🎭 CONVVIEWER: Checking preloaded data for ${messageRecordIds.length} message IDs:`, messageRecordIds);
      const preloadedData = await getDataSource().getPreloadedConversation(messageRecordIds);
      if (preloadedData) {
        setConversationData(preloadedData);
        // Also cache it in local cache for consistency
        if (cacheKey) {
          conversationCache.set(cacheKey, preloadedData);
        }
        return;
      }

      // Check local cache second
      if (cacheKey && conversationCache.has(cacheKey)) {
        const cachedData = conversationCache.get(cacheKey)!;
        setConversationData(cachedData);
        return;
      }

      // Fall back to ultra-fast single conversation loading
      setLoading(true);
      setError(null);

      try {
        const result = await getDataSource().getConversation(messageRecordIds, leadName);
        
        // Also cache in local cache for consistency
        if (cacheKey) {
          conversationCache.set(cacheKey, result);
        }
        
        setConversationData(result);
      } catch (err) {
        console.error('Error fetching conversation:', err);
        setError(t('conversation.errorLoadingConversation'));
        setConversationData({ messages: [], conversationSubject: '' });
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [messageRecordIds, cacheKey, leadName, t]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-8 ${className}`}>
        <Loader2 className="w-5 h-5 animate-spin text-gray-500 mr-2" />
        <span className="text-sm text-gray-500">{t('conversation.loadingConversation')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm text-red-500 py-4 ${className}`}>
        {error}
      </div>
    );
  }

  if (!conversationData || conversationData.messages.length === 0) {
    return (
      <div className={`text-sm text-gray-500 py-4 ${className}`}>
        {t('conversation.noConversation')}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {conversationData.conversationSubject && (
        <div className="conversation-subject bg-white border-b border-gray-200 py-4 px-0 text-sm font-semibold text-gray-700">
          {conversationData.conversationSubject}
        </div>
      )}
      
      <div className="messages-container rounded-md overflow-hidden bg-gray-100">
        {conversationData.messages.map((message) => (
          <MessageCard 
            key={message.id} 
            message={message} 
            processingFiles={messagesWithProcessingFiles?.[message.id]}
          />
        ))}
      </div>
    </div>
  );
});

ConversationViewer.displayName = 'ConversationViewer';